/**
 * Standard curves for protein assays and ELISA: straight-line and four-parameter logistic.
 *
 * Linear:  y = a + b·x   (ordinary least squares).
 * 4PL:     y = d + (a − d) ÷ (1 + (x ÷ c)^b)
 *          a = response at zero concentration, d = response at infinite concentration,
 *          c = the concentration at the midpoint (EC₅₀ / inflection), b = slope factor
 *          (Hill slope), b > 0. This is the form most ELISA readers and software use.
 *
 * The 4PL is fitted by Levenberg–Marquardt on (a, d, ln b, ln c), which keeps b and c
 * positive, from several starting slopes; the best fit is kept. Unknowns are read back
 * through the inverse of the fitted curve.
 */

import { parseNumberInput, parseNumberList } from "@/lib/parse-number";

export type FitKind = "linear" | "4pl";

export interface Point {
  x: number;
  y: number;
}

export interface LinearFit {
  kind: "linear";
  intercept: number;
  slope: number;
  r2: number;
  rmse: number;
  n: number;
}

export interface FourPlFit {
  kind: "4pl";
  /** Response at zero concentration. */
  a: number;
  /** Slope factor. */
  b: number;
  /** Midpoint concentration. */
  c: number;
  /** Response at infinite concentration. */
  d: number;
  r2: number;
  rmse: number;
  n: number;
}

export type Fit = LinearFit | FourPlFit;

export type FitError =
  | "points"
  | "distinct"
  | "flat"
  | "fourpl-points"
  | "fourpl-failed";

const MIN_LINEAR_POINTS = 3;
const MIN_4PL_POINTS = 4;
const SINGULAR = 1e-300;

function sum(values: readonly number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

function goodness(
  points: readonly Point[],
  predict: (x: number) => number,
  params: number
): { r2: number; rmse: number } {
  const meanY = sum(points.map((p) => p.y)) / points.length;
  const sst = sum(points.map((p) => (p.y - meanY) ** 2));
  const sse = sum(points.map((p) => (p.y - predict(p.x)) ** 2));
  const dof = points.length - params;
  return {
    r2: sst > 0 ? 1 - sse / sst : 1,
    rmse: dof > 0 ? Math.sqrt(sse / dof) : Number.NaN,
  };
}

export function fitLinear(
  points: readonly Point[]
): LinearFit | { error: FitError } {
  if (points.length < MIN_LINEAR_POINTS) {
    return { error: "points" };
  }
  const n = points.length;
  const mx = sum(points.map((p) => p.x)) / n;
  const my = sum(points.map((p) => p.y)) / n;
  const sxx = sum(points.map((p) => (p.x - mx) ** 2));
  if (sxx === 0) {
    return { error: "distinct" };
  }
  const sxy = sum(points.map((p) => (p.x - mx) * (p.y - my)));
  const slope = sxy / sxx;
  if (slope === 0) {
    return { error: "flat" };
  }
  const intercept = my - slope * mx;
  const { r2, rmse } = goodness(points, (x) => intercept + slope * x, 2);
  return { kind: "linear", intercept, slope, r2, rmse, n };
}

/** The 4PL response at concentration x. */
export function fourPl(
  x: number,
  a: number,
  b: number,
  c: number,
  d: number
): number {
  if (x <= 0) {
    return a;
  }
  return d + (a - d) / (1 + Math.exp(b * Math.log(x / c)));
}

type Theta = [number, number, number, number];

function modelTheta(theta: Theta, x: number): number {
  return fourPl(x, theta[0], Math.exp(theta[2]), Math.exp(theta[3]), theta[1]);
}

function sse(points: readonly Point[], theta: Theta): number {
  return sum(points.map((p) => (p.y - modelTheta(theta, p.x)) ** 2));
}

/** Solve a 4×4 linear system by Gaussian elimination with partial pivoting. */
function solve4(m: number[][], v: number[]): number[] | null {
  const a = m.map((row, i) => [...row, v[i]]);
  for (let col = 0; col < 4; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < 4; r += 1) {
      if (Math.abs(a[r][col]) > Math.abs(a[pivot][col])) {
        pivot = r;
      }
    }
    if (Math.abs(a[pivot][col]) < SINGULAR) {
      return null;
    }
    [a[col], a[pivot]] = [a[pivot], a[col]];
    for (let r = col + 1; r < 4; r += 1) {
      const factor = a[r][col] / a[col][col];
      for (let k = col; k < 5; k += 1) {
        a[r][k] -= factor * a[col][k];
      }
    }
  }
  const out = [0, 0, 0, 0];
  for (let r = 3; r >= 0; r -= 1) {
    let acc = a[r][4];
    for (let k = r + 1; k < 4; k += 1) {
      acc -= a[r][k] * out[k];
    }
    out[r] = acc / a[r][r];
  }
  return out;
}

function jacobian(points: readonly Point[], theta: Theta): number[][] {
  return points.map((p) => {
    const row = [0, 0, 0, 0];
    for (let j = 0; j < 4; j += 1) {
      const h = 1e-6 * Math.max(1, Math.abs(theta[j]));
      const up = [...theta] as Theta;
      const down = [...theta] as Theta;
      up[j] += h;
      down[j] -= h;
      row[j] = (modelTheta(up, p.x) - modelTheta(down, p.x)) / (2 * h);
    }
    return row;
  });
}

function stepOnce(
  points: readonly Point[],
  theta: Theta,
  lambda: number
): Theta | null {
  const jac = jacobian(points, theta);
  const res = points.map((p) => p.y - modelTheta(theta, p.x));
  const jtj = [0, 1, 2, 3].map((i) =>
    [0, 1, 2, 3].map((j) => sum(jac.map((row) => row[i] * row[j])))
  );
  const jtr = [0, 1, 2, 3].map((i) =>
    sum(jac.map((row, k) => row[i] * res[k]))
  );
  for (let i = 0; i < 4; i += 1) {
    jtj[i][i] += lambda * Math.max(jtj[i][i], 1e-12);
  }
  const delta = solve4(jtj, jtr);
  if (delta === null) {
    return null;
  }
  return theta.map((t, i) => t + delta[i]) as Theta;
}

function refine(
  points: readonly Point[],
  start: Theta
): { theta: Theta; sse: number } {
  let theta = start;
  let best = sse(points, theta);
  let lambda = 1e-2;
  for (let iter = 0; iter < 300; iter += 1) {
    const next = stepOnce(points, theta, lambda);
    const nextSse =
      next === null ? Number.POSITIVE_INFINITY : sse(points, next);
    if (next !== null && Number.isFinite(nextSse) && nextSse < best) {
      const improvement = best - nextSse;
      theta = next;
      best = nextSse;
      lambda = Math.max(lambda / 3, 1e-12);
      if (improvement < 1e-15 * Math.max(best, 1e-30)) {
        break;
      }
    } else {
      lambda *= 4;
      if (lambda > 1e12) {
        break;
      }
    }
  }
  return { theta, sse: best };
}

function endAverage(sorted: readonly Point[], fromEnd: boolean): number {
  const take = Math.max(1, Math.floor(sorted.length / 4));
  const slice = fromEnd ? sorted.slice(-take) : sorted.slice(0, take);
  return sum(slice.map((p) => p.y)) / slice.length;
}

function midpointX(sorted: readonly Point[], a: number, d: number): number {
  const target = (a + d) / 2;
  const positive = sorted.filter((p) => p.x > 0);
  let best = positive[0];
  for (const p of positive) {
    if (Math.abs(p.y - target) < Math.abs(best.y - target)) {
      best = p;
    }
  }
  return best.x;
}

export function fit4pl(
  points: readonly Point[]
): FourPlFit | { error: FitError } {
  if (points.length < MIN_4PL_POINTS) {
    return { error: "fourpl-points" };
  }
  const positiveX = new Set(points.filter((p) => p.x > 0).map((p) => p.x));
  const distinct = new Set(points.map((p) => p.x));
  if (distinct.size < MIN_4PL_POINTS || positiveX.size < MIN_4PL_POINTS - 1) {
    return { error: "distinct" };
  }
  const sorted = [...points].sort((p, q) => p.x - q.x);
  const a0 = endAverage(sorted, false);
  const d0 = endAverage(sorted, true);
  if (a0 === d0) {
    return { error: "flat" };
  }
  const c0 = midpointX(sorted, a0, d0);
  let best: { theta: Theta; sse: number } | null = null;
  for (const b0 of [0.7, 1.2, 2.5]) {
    const start: Theta = [a0, d0, Math.log(b0), Math.log(c0)];
    const fitted = refine(points, start);
    if (best === null || fitted.sse < best.sse) {
      best = fitted;
    }
  }
  if (best === null || !Number.isFinite(best.sse)) {
    return { error: "fourpl-failed" };
  }
  const [a, d, lnB, lnC] = best.theta;
  const b = Math.exp(lnB);
  const c = Math.exp(lnC);
  if (![a, b, c, d].every(Number.isFinite)) {
    return { error: "fourpl-failed" };
  }
  const { r2, rmse } = goodness(points, (x) => fourPl(x, a, b, c, d), 4);
  return { kind: "4pl", a, b, c, d, r2, rmse, n: points.length };
}

export function fitCurve(
  kind: FitKind,
  points: readonly Point[]
): Fit | { error: FitError } {
  return kind === "linear" ? fitLinear(points) : fit4pl(points);
}

/** Response the fitted curve predicts at concentration x. */
export function predict(fit: Fit, x: number): number {
  return fit.kind === "linear"
    ? fit.intercept + fit.slope * x
    : fourPl(x, fit.a, fit.b, fit.c, fit.d);
}

/** Concentration that gives response y, or null when the curve cannot reach it. */
export function interpolate(fit: Fit, y: number): number | null {
  if (fit.kind === "linear") {
    return (y - fit.intercept) / fit.slope;
  }
  const ratio = (fit.a - fit.d) / (y - fit.d);
  if (!Number.isFinite(ratio) || ratio <= 1) {
    return null;
  }
  return fit.c * (ratio - 1) ** (1 / fit.b);
}

export type SampleStatus = "within" | "above" | "below" | "unreachable";

export interface SampleResult {
  label: string | null;
  reading: number;
  /** Concentration read from the curve, before the dilution factor. */
  concentration: number | null;
  /** Concentration times the dilution factor. */
  original: number | null;
  status: SampleStatus;
}

export function readSamples(
  fit: Fit,
  points: readonly Point[],
  samples: readonly { label: string | null; value: number }[],
  dilution: number
): SampleResult[] {
  const ys = points.map((p) => p.y);
  const low = Math.min(...ys);
  const high = Math.max(...ys);
  return samples.map((s) => {
    const concentration = interpolate(fit, s.value);
    let status: SampleStatus = "within";
    if (concentration === null || concentration < 0) {
      status = concentration === null ? "unreachable" : "below";
    } else if (s.value > high) {
      status = "above";
    } else if (s.value < low) {
      status = "below";
    }
    // A response past a 4PL asymptote has no concentration; keep the status honest.
    return {
      label: s.label,
      reading: s.value,
      concentration,
      original: concentration === null ? null : concentration * dilution,
      status,
    };
  });
}

export interface BackCalc {
  x: number;
  y: number;
  back: number | null;
  /** back ÷ nominal × 100; null for a zero standard. */
  recovery: number | null;
}

export function backCalculate(fit: Fit, points: readonly Point[]): BackCalc[] {
  return points.map((p) => {
    const back = interpolate(fit, p.y);
    return {
      x: p.x,
      y: p.y,
      back,
      recovery: back !== null && p.x > 0 ? (back / p.x) * 100 : null,
    };
  });
}

const LINE_BREAK = /\r?\n/;

export function parseStandards(text: string): {
  points: Point[];
  skipped: number;
} {
  const points: Point[] = [];
  let skipped = 0;
  for (const line of text.split(LINE_BREAK)) {
    if (line.trim() === "") {
      continue;
    }
    const values = parseNumberList(line);
    if (values.length >= 2) {
      points.push({ x: values[0], y: values[1] });
    } else {
      skipped += 1;
    }
  }
  return { points, skipped };
}

const LABELLED_SAMPLE = /^\s*([^\d\-+.,;\s][^,;\t]*?)\s*[,;\t]\s*(.+)$/;

export function parseSamples(text: string): {
  samples: { label: string | null; value: number }[];
  skipped: number;
} {
  const samples: { label: string | null; value: number }[] = [];
  let skipped = 0;
  for (const line of text.split(LINE_BREAK)) {
    if (line.trim() === "") {
      continue;
    }
    const labelled = LABELLED_SAMPLE.exec(line);
    const value = labelled
      ? parseNumberInput(labelled[2])
      : (parseNumberList(line)[0] ?? null);
    if (value === null) {
      skipped += 1;
    } else {
      samples.push({ label: labelled ? labelled[1].trim() : null, value });
    }
  }
  return { samples, skipped };
}
